/**
 * Auth context — the app's single source of "who is signed in".
 *
 * On mount, if a token is already stored we hydrate the caller identity via GET /me. login()
 * exchanges credentials for tokens and hydrates; logout() clears storage and returns to /login.
 * The network layer (api.ts) transparently refreshes expired access tokens.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Persona } from "@talenthop/shared";
import { api } from "./api.js";
import { clearSession, getSession, setSession, type Session } from "./auth.js";

/** GET /me — identity proven by the token (never trusted from the client). */
export interface Me {
  userId: string;
  persona: Persona;
  orgId: string | null;
  role: string | null;
  impersonating: string | null;
  /** For the avatar and greetings — absent on tokens minted before the field shipped. */
  name?: string | null;
  email?: string | null;
}

interface LoginResponse {
  accessToken?: string;
  refreshToken?: string;
  tokenType?: string;
  persona?: Persona;
  /** MFA step-up: present (with mfaToken) when the password alone doesn't mint tokens. */
  mfaRequired?: boolean;
  mfaToken?: string;
  /** Which second factor to prompt for: 'email' (mailed code) or 'totp' (authenticator). */
  method?: "email" | "totp";
}

/** Either a completed sign-in, or an MFA challenge to finish via completeMfa(). */
export type LoginResult =
  | { persona: Persona }
  | { mfaRequired: true; mfaToken: string; method?: "email" | "totp" };

export interface AuthContextValue {
  session: Session | null;
  persona: Persona | null;
  me: Me | null;
  /** true while the initial GET /me hydration is in flight. */
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  /** Second step of an MFA login: POST /auth/mfa with the pending token + TOTP code. */
  completeMfa: (mfaToken: string, code: string) => Promise<Persona>;
  logout: () => void;
  /** Re-fetch /me — call after something changes the caller's own identity (e.g. Profile's
   *  rename) so the nav avatar/greeting update without a full reload. */
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();
  // Initialised synchronously from storage so guards can decide before /me resolves.
  const [session, setSessionState] = useState<Session | null>(() => getSession());
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState<boolean>(() => getSession() !== null);

  // Hydrate identity once on mount when a token is already present.
  useEffect(() => {
    let cancelled = false;
    if (!getSession()) {
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        const identity = await api.get<Me>("/me");
        if (!cancelled) setMe(identity);
      } catch {
        // A refresh failure already cleared+redirected; for any other error, drop the session.
        if (!cancelled) {
          clearSession();
          setSessionState(null);
          setMe(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Persist tokens + hydrate /me — shared by the password and MFA completion paths.
   *
   * QA #63 — "session data isolation": logout and impersonation start/stop all force a full
   * `window.location.assign`, which throws away every in-memory cache (including this app's
   * single module-level QueryClient) for free. Logging IN does not reload — it's a plain SPA
   * navigation — so it was the one identity-changing path that could leave a PRIOR user's cached
   * query results (dashboard, candidate lists, …) sitting under un-scoped query keys for a
   * fresh sign-in to briefly render before the real refetch lands. Only reachable by signing in
   * again without using Sign out first (e.g. a stale tab, or a shared machine), but the fix
   * costs nothing on the normal path: a fresh login has nothing worth keeping cached anyway.
   */
  const adoptSession = useCallback(async (res: LoginResponse): Promise<Persona> => {
    queryClient.clear();
    const next: Session = {
      accessToken: res.accessToken!,
      refreshToken: res.refreshToken!,
      tokenType: res.tokenType ?? "Bearer",
      persona: res.persona!,
    };
    setSession(next);
    setSessionState(next);
    try {
      setMe(await api.get<Me>("/me"));
    } catch {
      setMe(null);
    }
    return res.persona!;
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const res = await api.post<LoginResponse>("/auth/login", { email, password });
      if (res.mfaRequired && res.mfaToken) {
        return { mfaRequired: true, mfaToken: res.mfaToken, method: res.method };
      }
      return { persona: await adoptSession(res) };
    },
    [adoptSession],
  );

  const completeMfa = useCallback(
    async (mfaToken: string, code: string): Promise<Persona> => {
      const res = await api.post<LoginResponse>("/auth/mfa", { mfaToken, code });
      return adoptSession(res);
    },
    [adoptSession],
  );

  const logout = useCallback(() => {
    clearSession();
    setSessionState(null);
    setMe(null);
    if (typeof window !== "undefined") window.location.assign("/login");
  }, []);

  const refreshMe = useCallback(async (): Promise<void> => {
    try {
      setMe(await api.get<Me>("/me"));
    } catch {
      // Best-effort — a stale name/avatar isn't worth signing anyone out over.
    }
  }, []);

  const value: AuthContextValue = {
    session,
    persona: session?.persona ?? null,
    me,
    loading,
    login,
    completeMfa,
    logout,
    refreshMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
